import { RawDraftContentState } from 'draft-js';
import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { ANALYSIS_MARGIN, DATASET_INSTANCE_HEADER, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, useSplitView } from '../../../hooks/useSplitView';
import { getRiskToleranceIndicator, getSmileyIcon } from '../../admin/risk-tolerance/utils';
import { FeaturePermission } from '../../admin/users/store';
import { getUser, getUserHasFeaturePermission } from '../../auth/login/store';
import { getDocumentAnalysisView } from '../../documents/my-documents/store';
import { Button } from '../../shared/button/Button';
import { SpaceBetweenButtonWrapper } from '../../shared/button/SpaceBetweenButtonWrapper';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { isEmpty } from '../../shared/wysiwyg/WYSIWYG';
import styles from './DatasetInstance.module.scss';
import { DocumentRiskOverrideConversation } from './DocumentRiskOverrideConversation';
import { DocumentRiskOverrideInput } from './DocumentRiskOverrideInput';
import { DatasetModal } from './modal-instance/DatasetModal';
import { DocumentFieldRiskOverride, getClientRiskConversation, getConversationMaxDate, getIsEditing, getIsUpdating, overrideDocumentRiskScoreStarted, setCurrentClientRiskConversation, toggleDocumentRiskOverride } from './store';
import { isBefore } from '../../../utils/luxon';

const MODAL_HEADER_HEIGHT = 78;
const MODAL_PADDING = 20;
const TITLE_MARGIN = 40;
const BUTTON_WRAPPER = 48;
const JUSTIFICATION_INPUT_WRAPPER = 220;
const RISK_LEVEL_INPUT_WRAPPER = 80;
const heightOffset = NAV_BAR + MODAL_HEADER_HEIGHT + MODAL_PADDING + TITLE_MARGIN + BUTTON_WRAPPER;
const widthOffset = PAGE_MARGIN_PADDING_BORDER;

interface DocumentRiskOverrideModalProps {
    documentRiskOverride: DocumentFieldRiskOverride;
}

export const DocumentRiskOverrideModal: React.FC<DocumentRiskOverrideModalProps> = ({ documentRiskOverride }) => {
    const dispatch = useAppDispatch();
    const { fieldRiskConfig, fieldType, value, label } = documentRiskOverride;
    const documentAnalysisView = useAppSelector(getDocumentAnalysisView);
    const isEditing = useAppSelector(getIsEditing);
    const isUpdating = useAppSelector(getIsUpdating);
    const user = useAppSelector(getUser);
    const currentClientRiskConversation = useAppSelector(getClientRiskConversation);
    const conversationMaxDate = useAppSelector(getConversationMaxDate);
    const hasModifyDocumentRiskTolerancePermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.MODIFY_DOCUMENT_RISK_TOLERANCE]));
    const { justification, overrideRiskLevel } = currentClientRiskConversation;

    const testId = 'document-risk-override';

    const clientRiskConversation = useMemo(() => isNull(conversationMaxDate) ? documentRiskOverride.clientRiskConversation : documentRiskOverride.clientRiskConversation.filter(({ documentExecutedDate }) => isBefore(documentExecutedDate, conversationMaxDate)), [conversationMaxDate, documentRiskOverride.clientRiskConversation]);

    const isReadOnly = useMemo(() => !hasModifyDocumentRiskTolerancePermission || (isEditing || isUpdating), [hasModifyDocumentRiskTolerancePermission, isEditing, isUpdating]);
    const isEditingInstanceWithPermission = useMemo(() => hasModifyDocumentRiskTolerancePermission && (isEditing || isUpdating), [hasModifyDocumentRiskTolerancePermission, isEditing, isUpdating]);

    const closeModal = useCallback(() => dispatch(toggleDocumentRiskOverride(null)), [dispatch]);

    const [width, height] = useSplitView(documentAnalysisView, widthOffset, heightOffset);

    const inputWrapperHeight = useMemo(() => !isUndefined(overrideRiskLevel) ? JUSTIFICATION_INPUT_WRAPPER : RISK_LEVEL_INPUT_WRAPPER, [overrideRiskLevel]);
    const conversationHeightOffset = useMemo(() => (isReadOnly && !isEditingInstanceWithPermission) ? BUTTON_WRAPPER : BUTTON_WRAPPER + inputWrapperHeight, [isReadOnly, isEditingInstanceWithPermission, inputWrapperHeight]);
    const conversationHeight = useMemo(() => height - conversationHeightOffset, [height, conversationHeightOffset]);

    const { indicator, color, fill } = getRiskToleranceIndicator(fieldRiskConfig, value, fieldType, clientRiskConversation);

    const icon = getSmileyIcon(indicator);

    const updateJustification = useCallback((rawValue: RawDraftContentState) => {
        if (isEmpty(rawValue)) {
            if (!isNull(justification)) {
                dispatch(setCurrentClientRiskConversation({ justification: null, overrideRiskLevel }));
            }
        } else {
            dispatch(setCurrentClientRiskConversation({ justification: rawValue, overrideRiskLevel }));
        }
    }, [dispatch, overrideRiskLevel, justification]);

    const overrideRiskScore = useCallback(() => dispatch(overrideDocumentRiskScoreStarted()), [dispatch]);

    const rightButtons = useMemo(() => {
        if (isEditingInstanceWithPermission) {
            return [];
        }
        return [<Button onClick={overrideRiskScore} label='Overwrite Risk' key='overwrite' disabled={isUndefined(overrideRiskLevel)} />];
    }, [overrideRiskLevel, isEditingInstanceWithPermission, overrideRiskScore]);

    return (
        <DatasetModal
            isOpen
            closeModal={closeModal}
            topOffset={`${DATASET_INSTANCE_HEADER + NAV_BAR + ANALYSIS_MARGIN}px`}
        >
            <div className={styles.documentRiskOverrideWrapper} data-testid='annex-config-wrapper' style={{ width }}>
                <ModalHeader label={`Override Risk Tolerance Score: ${label}`} icon={icon} iconFill={fill} iconColor={color} />
                <div className={styles.documentRiskOverride} style={{ height: `${height}px` }}>
                    <DocumentRiskOverrideConversation
                        conversationHeight={conversationHeight}
                        clientRiskConversation={clientRiskConversation}
                        user={user!}
                        testId={testId}
                        isReadOnly={isReadOnly}
                    />
                    {!isReadOnly &&
                        <div className={styles.riskSelectionAndInput} style={{ height: `${inputWrapperHeight}px` }}>
                            <DocumentRiskOverrideInput
                                justification={justification}
                                updateJustification={updateJustification}
                                overrideRiskLevel={overrideRiskLevel}
                                clientRiskConversation={clientRiskConversation}
                                documentRiskOverride={documentRiskOverride}
                            />
                        </div>
                    }
                    {isEditingInstanceWithPermission &&
                        <div className={styles.editingInstanceWarning} style={{ height: `${inputWrapperHeight}px` }}>
                            Risk cannot be overwritten whilst {isEditing ? 'editing' : 'updating'} the dataset
                        </div>
                    }
                    <SpaceBetweenButtonWrapper leftButtons={[<Button onClick={closeModal} label='Close' key='close' />]} rightButtons={rightButtons} />
                </div>
            </div>
        </DatasetModal>
    );
};
